import React, { useCallback, useEffect, useRef, useState } from "react";

// Pinch-to-zoom + drag-to-pan wrapper used for shared content, exactly like
// Zoom's mobile share view: two-finger pinch zooms, one finger pans once
// zoomed in, double tap toggles 2x, ctrl+wheel zooms on desktop.
const MAX = 4;

export const ZoomPan = ({ children, className = "relative", testid, resetSignal }) => {
  const boxRef = useRef(null);
  const [t, setT] = useState({ s: 1, x: 0, y: 0 });
  // Mirror of `t` so gesture handlers always read the live transform without
  // depending on a stale render closure.
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);
  const pointers = useRef(new Map());
  const gesture = useRef(null);

  useEffect(() => {
    setT({ s: 1, x: 0, y: 0 });
  }, [resetSignal]);

  const clamp = useCallback((next) => {
    const s = Math.min(MAX, Math.max(1, Number.isFinite(next?.s) ? next.s : 1));
    const el = boxRef.current;
    const w = el ? el.clientWidth : 0;
    const h = el ? el.clientHeight : 0;
    const maxX = ((s - 1) * w) / 2;
    const maxY = ((s - 1) * h) / 2;
    const nx = Number.isFinite(next?.x) ? next.x : 0;
    const ny = Number.isFinite(next?.y) ? next.y : 0;
    return {
      s,
      x: Math.min(maxX, Math.max(-maxX, s === 1 ? 0 : nx)),
      y: Math.min(maxY, Math.max(-maxY, s === 1 ? 0 : ny)),
    };
  }, []);

  const startPanGesture = (x, y) => {
    const cur = tRef.current;
    gesture.current = { pan: { x, y }, x: cur.x, y: cur.y };
  };

  const onPointerDown = (e) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const cur = tRef.current;
    if (pointers.current.size >= 2) {
      const [a, b] = Array.from(pointers.current.values());
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      gesture.current = { dist: dist || 1, s: cur.s, x: cur.x, y: cur.y };
    } else {
      startPanGesture(e.clientX, e.clientY);
    }
  };

  const onPointerMove = (e) => {
    const g = gesture.current;
    if (!g) return;
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const count = pointers.current.size;

    if (count >= 2 && g.dist) {
      const [a, b] = Array.from(pointers.current.values());
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (!dist) return;
      const ratio = dist / g.dist;
      setT((prev) => clamp({ ...prev, s: g.s * ratio }));
      return;
    }

    if (count === 1 && g.pan && tRef.current.s > 1) {
      const dx = e.clientX - g.pan.x;
      const dy = e.clientY - g.pan.y;
      setT((prev) => clamp({ ...prev, x: g.x + dx, y: g.y + dy }));
    }
  };

  const endPointer = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size === 1) {
      // A finger lifted out of a pinch — hand the remaining one over to pan
      // instead of leaving a stale pinch gesture behind.
      const [p] = Array.from(pointers.current.values());
      startPanGesture(p.x, p.y);
    } else if (pointers.current.size === 0) {
      gesture.current = null;
    }
  };

  const onWheel = (e) => {
    if (!e.ctrlKey) return;
    e.preventDefault();
    setT((prev) => clamp({ ...prev, s: prev.s * (e.deltaY < 0 ? 1.12 : 0.9) }));
  };

  const onDoubleClick = (e) => {
    e.stopPropagation();
    setT((prev) => clamp({ s: prev.s > 1 ? 1 : 2, x: 0, y: 0 }));
  };

  return (
    <div
      ref={boxRef}
      className={`overflow-hidden touch-none ${className}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
      onWheel={onWheel}
      onDoubleClick={onDoubleClick}
      data-testid={testid}
      data-zoom={t.s.toFixed(2)}
    >
      <div
        className="absolute inset-0"
        style={{
          transform: `translate3d(${t.x}px, ${t.y}px, 0) scale(${t.s})`,
          transformOrigin: "center center",
          transition: gesture.current ? "none" : "transform 160ms ease-out",
        }}
      >
        {children}
      </div>
      {t.s > 1 && (
        <button
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setT({ s: 1, x: 0, y: 0 });
          }}
          className="absolute top-3 right-3 z-20 h-8 px-3 rounded-full bg-black/75 border border-emerald-500/50 text-emerald-300 text-[10px] uppercase tracking-widest font-semibold"
          data-testid="share-zoom-reset"
        >
          {t.s.toFixed(1)}x · reset
        </button>
      )}
    </div>
  );
};

export default ZoomPan;
